import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import InnerPageHero from '@/components/feature/InnerPageHero';
import { useEnquiryModal } from '@/hooks/useEnquiryModal';
import FadeInSection from '@/components/base/FadeInSection';

const whyChooseUs = [
  {
    icon: 'ri-shield-check-line',
    title: 'Fresh Quality',
    description: 'Every product is carefully handpicked and inspected to ensure only the freshest and highest quality produce reaches your business.',
  },
  {
    icon: 'ri-truck-line',
    title: 'Reliable Supply',
    description: 'Consistent and dependable supply chain ensuring your business never faces shortages. We deliver on time, every time.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Competitive Pricing',
    description: 'Fair and transparent pricing on bulk orders with no hidden costs. We offer market-competitive rates for long-term partnerships.',
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'Long-Term Partnership',
    description: 'We believe in building lasting business relationships based on trust, quality, and mutual growth — not just one-time transactions.',
  },
];

export default function About() {
  const { openEnquiry } = useEnquiryModal();

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Page Header */}
        <InnerPageHero
          bgImage="/images/about-hero.jpg"
          bgAlt="Bassi Enterprises warehouse and fresh produce supply"
          bgTitle="Bassi Enterprises - Fresh Fruits & Vegetables Supplier Shamli Uttar Pradesh"
          breadcrumbLabel="About Us"
        >
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background-50/15 backdrop-blur-sm text-background-50 rounded-full text-xs font-semibold mb-5 border border-background-50/15">
              <i className="ri-building-2-line"></i>
              Our Company
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 leading-tight">
              About Bassi Enterprises
            </h1>
            <p className="text-background-50/50 text-sm md:text-base font-semibold mb-4 tracking-wide uppercase">
              Reliable Fresh Fruits &amp; Vegetables Supplier
            </p>
            <p className="text-background-200 text-base md:text-lg leading-relaxed mb-8">
              Delivering quality produce with consistency, transparency and professional business service. Your trusted partner for farm-fresh supply across India.
            </p>
            <button
              onClick={() => openEnquiry()}
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 hover:bg-primary-400 text-background-50 font-semibold rounded-full text-base transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-fill text-xl"></i>
              Request Bulk Supply Quote
            </button>
          </div>
        </InnerPageHero>

        {/* Company Story */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-10">
          <FadeInSection>
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                <div className="relative">
                  <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden bg-background-100">
                    <img
                      src="/images/about-company-photo.jpg"
                      alt="Bassi Enterprises Warehouse"
                      title="Bassi Enterprises - Fresh Produce Supply"
                      loading="lazy"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="absolute -bottom-4 -right-4 w-24 h-24 md:w-32 md:h-32 bg-accent-500 rounded-2xl flex items-center justify-center">
                    <div className="text-center text-background-50">
                      <span className="block font-heading font-bold text-2xl md:text-3xl leading-none">10+</span>
                      <span className="block text-[10px] md:text-xs mt-1">Years of<br/>Trust</span>
                    </div>
                  </div>
                </div>
                <div>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold mb-5">
                    <i className="ri-building-2-line"></i>
                    Our Story
                  </span>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                    Committed to Freshness & Quality Since Day One
                  </h2>
                  <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                    <p>
                      Bassi Enterprises is committed to supplying fresh fruits and vegetables with quality, reliability, and competitive pricing. Based in Shamli, Uttar Pradesh, we serve as a dedicated fresh produce supply partner for businesses requiring regular and bulk quantities.
                    </p>
                    <p>
                      Our goal is to become the most trusted fresh produce supply partner for businesses across India. We understand that in the food industry, quality and consistency are everything — and we deliver on both.
                    </p>
                    <p>
                      From quick-commerce platforms to restaurants, hotels, cloud kitchens, retailers, and wholesalers — we have built our reputation on dependable supply and professional service that businesses can count on.
                    </p>
                  </div>
                  <div className="mt-6 space-y-3">
                    {[
                      'Quality produce sourced from trusted farms',
                      'Timely availability and consistent supply',
                      'Business-to-business supply expertise',
                      'Long-term partnership approach',
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-primary-600 text-xs"></i>
                        </div>
                        <span className="text-foreground-700 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </FadeInSection>
        </section>

        {/* Mission & Vision */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-10 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <FadeInSection>
                <div className="bg-background-50 rounded-2xl p-8 md:p-10 border border-background-200">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-5">
                    <i className="ri-focus-2-line text-primary-600 text-xl"></i>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">Our Mission</h3>
                  <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                    To provide businesses across India with consistent, high-quality fresh produce through a reliable and efficient supply chain. We aim to bridge the gap between farms and food businesses with transparency, quality assurance, and exceptional service.
                  </p>
                </div>
              </FadeInSection>
              <FadeInSection delay={120}>
                <div className="bg-background-50 rounded-2xl p-8 md:p-10 border border-background-200">
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mb-5">
                    <i className="ri-eye-line text-accent-600 text-xl"></i>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">Our Vision</h3>
                  <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                    To become India&rsquo;s most trusted fresh produce supply partner, recognized for quality, integrity, and innovation. We envision a future where every food business has access to farm-fresh ingredients without compromise.
                  </p>
                </div>
              </FadeInSection>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            <FadeInSection>
              <div className="text-center mb-12">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold mb-4">
                  <i className="ri-star-line"></i>
                  Why Businesses Choose Us
                </span>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                  The Bassi Enterprises Difference
                </h2>
              </div>
            </FadeInSection>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {whyChooseUs.map((item, idx) => (
                <FadeInSection key={idx} delay={idx * 100}>
                  <div
                    className="group bg-background-50 border border-background-200 rounded-2xl p-6 md:p-7 hover:border-primary-300 transition-all duration-300"
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-4 group-hover:bg-primary-500 transition-colors duration-300">
                      <i className={`${item.icon} text-primary-600 group-hover:text-background-50 text-xl transition-colors duration-300`}></i>
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-foreground-500 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </FadeInSection>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-10 bg-primary-950 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-1/4 w-72 h-72 rounded-full bg-primary-400 blur-3xl"></div>
            <div className="absolute bottom-10 right-1/4 w-64 h-64 rounded-full bg-accent-500 blur-3xl"></div>
          </div>
          <FadeInSection>
            <div className="max-w-4xl mx-auto text-center relative z-10">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">
                Ready to Partner With Us?
              </h2>
              <p className="text-background-50/60 text-base md:text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
                Whether you run a restaurant, retail chain, or quick-commerce platform — we are ready to supply the freshest produce for your business.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="https://wa.me/919084756574?text=Hello%20Bassi%20Enterprises,%20I%20want%20to%20discuss%20business%20supply%20partnership"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 hover:bg-accent-400 text-background-50 font-semibold rounded-full text-base transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-fill text-xl"></i>
                  Become A Business Partner
                </a>
                <a
                  href="tel:+919084756574"
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50/10 hover:bg-background-50/20 text-background-50 border border-background-50/20 font-semibold rounded-full text-base transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-fill text-lg"></i>
                  Call Now
                </a>
              </div>
            </div>
          </FadeInSection>
        </section>
      </main>
      <Footer />
    </div>
  );
}